"use client"

import { createContext, Fragment, type ReactNode, useContext, useEffect, useMemo, useState } from "react"
import { ConsumerSessionMemory, nextConsumerSessionRenderState } from "../lib/consumer-session-memory.ts"

const ConsumerSessionContext = createContext<{ readonly memory: ConsumerSessionMemory; readonly revision: number } | null>(null)

export const useConsumerSessionMemory = (): ConsumerSessionMemory | null => useContext(ConsumerSessionContext)?.memory ?? null

export const ConsumerSessionProvider = ({ children }: { readonly children: ReactNode }) => {
  const [memory] = useState(() => new ConsumerSessionMemory())
  const [session, setSession] = useState({ visible: true, generation: memory.epoch, epoch: memory.epoch })
  const context = useMemo(() => ({ memory, revision: session.epoch }), [memory, session.epoch])

  useEffect(() => {
    const clearSession = (event: Event) => {
      memory.clear()
      const epoch = memory.epoch
      setSession((state) => nextConsumerSessionRenderState(state, "clearing", epoch, (event as CustomEvent<unknown>).detail))
    }
    const startSession = (event: Event) => {
      memory.clear()
      const epoch = memory.epoch
      setSession((state) => nextConsumerSessionRenderState(state, "ready", epoch, (event as CustomEvent<unknown>).detail))
    }
    window.addEventListener("cashhub:session-clearing", clearSession)
    window.addEventListener("cashhub:session-ready", startSession)
    return () => {
      window.removeEventListener("cashhub:session-clearing", clearSession)
      window.removeEventListener("cashhub:session-ready", startSession)
    }
  }, [memory])

  return <ConsumerSessionContext.Provider value={context}>{session.visible ? <Fragment key={session.generation}>{children}</Fragment> : null}</ConsumerSessionContext.Provider>
}
